import { create } from "zustand";
import { persist } from "zustand/middleware";
import { supabase } from "@/integrations/supabase/client";
import { useAuthStore } from "@/store/authStore";
import { fetchBrandSettings, updateBrandSettings } from "@/services/settings";

interface BrandColors {
  primary: string;
  secondary: string;
  accent: string;
}

interface BrandState {
  name: string;
  logo: string | null;
  colors: BrandColors;
  scope: "homeOnly" | "global"; // New property to control where colors apply
  setName: (name: string) => void;
  setLogo: (logo: string) => void;
  setColors: (colors: Partial<BrandColors>) => void;
  setScope: (scope: "homeOnly" | "global") => void;
  resetToDefaults: () => void;
  applyColorsToCss: () => void;
  syncWithDatabase: () => Promise<void>;
  loadFromDatabase: () => Promise<void>;
}

const defaultColors: BrandColors = {
  primary: "#6366F1",
  secondary: "#EC4899",
  accent: "#8B5CF6",
};

// Helper to convert hex to HSL values for CSS variables
const hexToHSL = (hex: string): string => {
  // Remove the # if present
  hex = hex.replace("#", "");

  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  // Find max and min RGB components
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h *= 60;
  }

  // Round values
  h = Math.round(h);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return `${h} ${s}% ${l}%`;
};

export const useBrandStore = create<BrandState>()(
  persist(
    (set, get) => ({
      name: "",
      logo: null,
      colors: { ...defaultColors },
      scope: "global", // Default to global scope for backward compatibility
      setName: (name) => set({ name }),
      setLogo: (logo) => set({ logo }),
      setScope: (scope) => set({ scope }),
      setColors: (colors) => {
        set((state) => ({
          colors: { ...state.colors, ...colors },
        }));
        // Apply colors immediately after state update
        setTimeout(() => get().applyColorsToCss(), 0);
      },
      resetToDefaults: () => {
        set({ colors: { ...defaultColors } });
        // Apply default colors immediately
        setTimeout(() => get().applyColorsToCss(), 0);
      },
      applyColorsToCss: () => {
        const { colors } = get();
        // Apply colors only if the current route is /dashboard or a subroute
        const isDashboardRoute =
          window.location.pathname.startsWith("/dashboard");
        if (isDashboardRoute) {
          document.documentElement.style.setProperty(
            "--primary",
            hexToHSL(colors.primary)
          );
          document.documentElement.style.setProperty(
            "--secondary",
            hexToHSL(colors.secondary)
          );
          document.documentElement.style.setProperty(
            "--accent",
            hexToHSL(colors.accent)
          );
        } else {
          // Reset to default colors when not on dashboard routes
          document.documentElement.style.setProperty(
            "--primary",
            hexToHSL(defaultColors.primary)
          );
          document.documentElement.style.setProperty(
            "--secondary",
            hexToHSL(defaultColors.secondary)
          );
          document.documentElement.style.setProperty(
            "--accent",
            hexToHSL(defaultColors.accent)
          );
        }
      },
      syncWithDatabase: async () => {
        const { user } = useAuthStore.getState();
        if (!user?.id) return;

        try {
          const { name, logo, colors, scope } = get();

          const brandData = {
            name,
            logo,
            primary_color: colors.primary,
            secondary_color: colors.secondary,
            accent_color: colors.accent,
            scope: scope,
          };

          await updateBrandSettings(user.id, brandData);
        } catch (error) {
          console.error("Error syncing brand settings with database:", error);
        }
      },
      loadFromDatabase: async () => {
        const { user } = useAuthStore.getState();
        if (!user?.id) return;

        try {
          const settings = await fetchBrandSettings(user.id);

          if (settings) {
            set({
              name: settings.name || "",
              logo: settings.logo,
              scope: (settings.scope as "homeOnly" | "global") || "global",
              colors: {
                primary: settings.primary_color,
                secondary: settings.secondary_color,
                accent: settings.accent_color,
              },
            });

            // Apply the fetched colors
            setTimeout(() => get().applyColorsToCss(), 0);
          }
        } catch (error) {
          console.error("Error loading brand settings from database:", error);
        }
      },
    }),
    {
      name: "Xerven-brand",
    }
  )
);

// Initialize the CSS variables when the app loads
if (typeof window !== "undefined") {
  useBrandStore.getState().applyColorsToCss();

  // Load settings from database on initialization
  setTimeout(() => {
    useBrandStore.getState().loadFromDatabase();
  }, 500); // Small delay to ensure auth is initialized

  // Add a route change listener to ensure colors are applied correctly when navigating
  window.addEventListener("popstate", () => {
    useBrandStore.getState().applyColorsToCss();
  });
}
